import moment, { DurationInputArg2 } from 'moment';

export const isSameDate = (metadata: {
    date: Undefinable<string>,
    unit: DurationInputArg2,
    toLocal?: boolean;
}): boolean => {
    if (metadata.toLocal) {
        return moment().isSame(moment.utc(metadata.date).local(), metadata.unit);
    } else {
        return moment().isSame(moment(metadata.date), metadata.unit);
    }
};

export const getCurrentDate = (destFormat?: Undefinable<string>): string => {
    return moment().format(destFormat || 'YYYY-MM-DDTHH:mm:ss');
};

export const getUTCCurrentDate = (metadata: {
    destFormat?: Undefinable<string>,
    delta?: {
        number: number;
        unit: DurationInputArg2;
    };
}): string => {
    const utcDate = moment().utc();
    if (metadata.delta) {
        utcDate.add(metadata.delta.number, metadata.delta.unit);
    }
    return utcDate.format(metadata.destFormat || 'YYYY-MM-DDTHH:mm:ss');
};

export const getUTCCurrentDateRange = (destFormat: string): string[] => {
    const startDate = moment().startOf('day');
    const endDate = moment(startDate).add(1, 'day');
    const utcStartDate = moment(startDate).utc().format(destFormat);
    const utcEndDate = moment(endDate).utc().format(destFormat);
    return [utcStartDate, utcEndDate];
};

export const formatDate = (metadata: {
    date?: Undefinable<string> | Date | null;
    destFormat?: string;
    srcFormat?: string;
    delta?: {
        number: number;
        unit: moment.DurationInputArg2;
    };
    current?: boolean;
    toLocal?: boolean;
}) => {
    let destFormat = metadata.destFormat || 'YYYY-MM-DDTHH:mm:ss';
    let momentDate: Undefinable<moment.Moment>;
    if (metadata.date) {
        momentDate = metadata.srcFormat ?
            moment(
                metadata.date,
                metadata.srcFormat
            ) :
            moment(metadata.date);
        if (metadata.delta) {
            momentDate = momentDate.add(
                metadata.delta.number,
                metadata.delta.unit
            );
        }
    } else if (
        metadata &&
        metadata.current
    ) {
        momentDate = moment();
        if (metadata.delta) {
            momentDate = momentDate.add(
                metadata.delta.number,
                metadata.delta.unit
            );
        }
    }

    if (momentDate) {
        if (metadata.toLocal) {
            return moment
                .utc(momentDate.format('YYYY-MM-DDTHH:mm:ss'))
                .local()
                .format(destFormat);
        } else {
            return momentDate.format(destFormat);
        }
    } else {
        return '';
    }
};

export const formatICD10 = (
    code?: string
) => {
    if (
        !code ||
        code.length <= 3
    ) {
        return code;
    }

    return `${code.substring(0, 3)}.${code.substring(3)}`;
};

export const formatName = (
    firstName: Undefinable<string>,
    lastName: Undefinable<string>
) => {
    return firstName ? `${lastName || ''}, ${firstName || ''}` : (lastName || '');
};

export const withDOB = (
    name: string,
    dob: Undefinable<string>
) => {
    if (dob) {
        const convertedDOB = moment(dob, 'YYYY-MM-DD').format('MM/DD/YY');
        return `${name} (${convertedDOB})`;
    } else {
        return name;
    }
};

export const withDegree = (
    name: string,
    degree: Undefinable<string>
) => {
    if (degree) {
        return `${name}, ${degree}`;
    } else {
        return name;
    }
};

export const formatAddress = (
    address: Undefinable<string>,
    city: Undefinable<string>,
    state: Undefinable<string>,
    zipcode: Undefinable<string>
) => {
    const data = [address || '', city || '', state || ''].filter(x => x);
    return `${data.join(', ')} ${zipcode || ''}`;
};

export const formatPhone = (
    phoneNumber: Undefinable<string>,
    type: 'mask' | 'unmask'
) => {
    if (phoneNumber) {
        const pureNumber = phoneNumber
            .replace(/\(/g, '')
            .replace(/\)/g, '')
            .replace(/-/g, '')
            .replace(/\s/g, '')
            .replace(/_/g, '');
        if (type === 'mask') {
            return `(${pureNumber.substr(0, 3)}) ${pureNumber.substr(3, 3)}-${pureNumber.substr(6)}`;
        } else if (type === 'unmask') {
            return pureNumber;
        }
    }

    return phoneNumber || '';
};

// always return true if one of 2 fields is missing
export const isDayNotGreaterThan = (
    from: Undefinable<string>,
    to: Undefinable<string>,
    format?: string
): boolean => {
    if (from && to) {
        if (format) {
            const formatFrom = formatDate({
                date: from,
                srcFormat: format,
                destFormat: 'YYYYMMDD'
            });
            const formatTo = formatDate({
                date: to,
                srcFormat: format,
                destFormat: 'YYYYMMDD'
            });
            return formatFrom <= formatTo;
        } else {
            return Number(from) <= Number(to);
        }
    } else {
        return true;
    }
};
